import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import {
  ArrowLeft,
  FileAudio,
  Upload,
  Loader2,
  Download,
  Trash2,
  FileText,
  ScrollText,
  ListChecks,
  Mic,
  Users,
  X,
  Folder,
  FolderOpen,
  Copy,
  Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMAT_META = [
  { key: "detailed", label: "Detailed", icon: ScrollText, field: "detailed_md" },
  { key: "summary", label: "Summary", icon: ListChecks, field: "summary_md" },
  { key: "meeting", label: "Meeting Notes", icon: FileText, field: "meeting_notes_md" },
];

function formatDuration(seconds) {
  const s = Math.floor(seconds || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function formatBytes(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

// File-extensions / MIME prefixes we treat as "needs MP3 transcoding".
// Anything else (mp3 / wav / m4a / etc.) goes straight to the backend.
const VIDEO_EXTS = new Set([
  "mp4", "mov", "mkv", "avi", "wmv", "flv", "3gp", "webm", "mpeg",
]);

function isVideoFile(file) {
  if (!file) return false;
  if ((file.type || "").startsWith("video/")) return true;
  const ext = (file.name || "").split(".").pop().toLowerCase();
  return VIDEO_EXTS.has(ext);
}

// ffmpeg.wasm singleton — we only want to download the ~32 MB core ONCE
// per session. After the first conversion the module + WASM are cached
// in memory; subsequent conversions reuse it.
let _ffmpegInstance = null;

/**
 * Load the @ffmpeg/ffmpeg UMD bundle as a <script> tag. We can't use the
 * ES-imported `FFmpeg` class because CRA's webpack rewrites the internal
 * `new URL('./814.ffmpeg.js', import.meta.url)` into a webpackEmptyContext
 * stub — which throws "Cannot find module 'blob:...'" at runtime and
 * bubbles up as a toast on production. Loading the UMD bundle from a
 * plain <script> tag (served from /ffmpeg/ on the same origin) side-steps
 * webpack entirely: the browser evaluates the file as-is, `import.meta.url`
 * resolves to the /ffmpeg/ffmpeg.js URL, and the sibling 814.ffmpeg.js
 * chunk resolves normally.
 */
function loadFFmpegUmdScript() {
  return new Promise((resolve, reject) => {
    if (window.FFmpegWASM?.FFmpeg) {
      resolve(window.FFmpegWASM.FFmpeg);
      return;
    }
    const existing = document.querySelector('script[data-ffmpeg-umd="1"]');
    if (existing) {
      // Another call is loading the script — wait for it.
      existing.addEventListener("load", () =>
        window.FFmpegWASM?.FFmpeg
          ? resolve(window.FFmpegWASM.FFmpeg)
          : reject(new Error("FFmpeg UMD loaded but global missing"))
      );
      existing.addEventListener("error", () => reject(new Error("FFmpeg UMD load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = `${window.location.origin}/ffmpeg/ffmpeg.js`;
    s.async = true;
    s.dataset.ffmpegUmd = "1";
    s.onload = () => {
      if (window.FFmpegWASM?.FFmpeg) resolve(window.FFmpegWASM.FFmpeg);
      else reject(new Error("FFmpeg UMD loaded but window.FFmpegWASM missing"));
    };
    s.onerror = () => reject(new Error("Could not load /ffmpeg/ffmpeg.js"));
    document.head.appendChild(s);
  });
}

async function getFFmpeg(onProgress, onLog) {
  if (_ffmpegInstance) return _ffmpegInstance;
  const FFmpegCtor = await loadFFmpegUmdScript();
  const ffmpeg = new FFmpegCtor();
  if (onLog) ffmpeg.on("log", ({ message }) => onLog(message));
  if (onProgress) ffmpeg.on("progress", ({ progress }) => onProgress(progress));
  // Self-host under /ffmpeg/ so mobile carriers that block CDNs still work.
  const base = `${window.location.origin}/ffmpeg`;
  await ffmpeg.load({
    coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, "text/javascript"),
    wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, "application/wasm"),
    classWorkerURL: await toBlobURL(`${base}/814.ffmpeg.js`, "text/javascript"),
  });
  _ffmpegInstance = ffmpeg;
  return ffmpeg;
}

/**
 * Convert a video file to a compact mp3 (mono, 16 kHz, 64 kbps) entirely
 * in the browser via ffmpeg.wasm. Returns a new File ready for upload.
 * For a 2 GB hour-long .mp4 this typically produces a ~30–40 MB .mp3,
 * which fits well within nginx limits and uploads ~70x faster.
 */
async function videoToMp3InBrowser(file, { onStage, onProgress }) {
  onStage("Loading converter (first time only, ~30 MB)…");
  const ff = await getFFmpeg((p) => {
    onProgress(Math.max(0, Math.min(1, p)));
  });

  onStage("Reading file…");
  const inputName = "input." + (file.name.split(".").pop().toLowerCase() || "mp4");
  await ff.writeFile(inputName, await fetchFile(file));

  onStage("Extracting audio → MP3…");
  await ff.exec([
    "-i", inputName,
    "-vn",                 // drop video track
    "-acodec", "libmp3lame",
    "-b:a", "64k",         // 64 kbps mono — perfectly fine for speech / Whisper
    "-ac", "1",
    "-ar", "16000",        // 16 kHz sample rate — Whisper's native rate
    "-f", "mp3",
    "output.mp3",
  ]);

  onStage("Finalising…");
  const data = await ff.readFile("output.mp3");
  // ff.readFile returns Uint8Array; wrap in a Blob backed File so axios
  // treats it as a multipart attachment.
  const baseName = (file.name.replace(/\.[^.]+$/, "") || "converted") + ".mp3";
  const mp3File = new File([data], baseName, { type: "audio/mpeg" });
  try {
    await ff.deleteFile(inputName);
    await ff.deleteFile("output.mp3");
  } catch {
    // Cleanup failures are non-fatal.
  }
  return mp3File;
}

/**
 * Light-weight markdown renderer just for what the LLM emits:
 *  - **bold text**
 *  - bullets:  "- " or "* "
 *  - numbered: "1)"  "1."
 *  - blank lines → paragraph breaks
 * Everything else renders as a plain line. Safer than dropping
 * `dangerouslySetInnerHTML` since we never inject raw HTML.
 */
function MarkdownView({ text, testId }) {
  if (!text) {
    return (
      <div
        data-testid={testId}
        className="text-slate-400 text-sm bg-slate-900 border border-slate-700 rounded-md p-4"
      >
        (empty — try uploading clearer audio)
      </div>
    );
  }

  const renderInline = (line) => {
    // split on **...** and bold the odd indexes
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        return (
          <strong key={i} className="text-white font-semibold">
            {p.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{p}</span>;
    });
  };

  const lines = text.split("\n");
  const out = [];
  let key = 0;
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      out.push(<div key={key++} className="h-2" />);
      continue;
    }
    const stripped = line.replace(/^\s+/, "");
    if (stripped.startsWith("- ") || stripped.startsWith("* ")) {
      out.push(
        <div key={key++} className="flex gap-2 pl-2">
          <span className="text-amber-400 shrink-0">•</span>
          <span className="break-words">{renderInline(stripped.slice(2))}</span>
        </div>
      );
    } else if (/^\d+[).]\s/.test(stripped)) {
      out.push(
        <div key={key++} className="pl-2 break-words">
          {renderInline(stripped)}
        </div>
      );
    } else {
      out.push(
        <div key={key++} className="break-words">
          {renderInline(stripped)}
        </div>
      );
    }
  }
  return (
    <div
      data-testid={testId}
      className="text-slate-200 text-sm sm:text-[15px] leading-relaxed bg-slate-900 border border-slate-700 rounded-md p-3 sm:p-4 max-h-[60vh] overflow-y-auto space-y-1"
    >
      {out}
    </div>
  );
}

export default function MeetingTranscriptions() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [transcriptions, setTranscriptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState("");
  const [meetingDate, setMeetingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  // Local-conversion progress — the user uploads a video, we ffmpeg.wasm it
  // down to a small mp3 BEFORE the network upload starts.
  const [conversionStage, setConversionStage] = useState(null);
  const [conversionProgress, setConversionProgress] = useState(0);
  // Attendees picker — closed-set roster the LLM can map speakers to.
  // Without this the prompt has no anchor for "who said what" and routinely
  // invents names. With it, names come exclusively from the roster.
  const [rosterMembers, setRosterMembers] = useState([]);
  const [attendeeHandles, setAttendeeHandles] = useState(new Set());
  const [attendeeFilter, setAttendeeFilter] = useState("");
  // Chapter folder filter — splits the transcription list by chapter so
  // National officers (who see all chapters) can drill into one at a time.
  // "All" means no filter; chapter-only users will only see their own
  // chapter's folder populated by virtue of the backend filtering.
  const [activeChapterFolder, setActiveChapterFolder] = useState("All");
  // Track which format was most recently copied so we can flash a check-mark
  // on that specific Copy button. Cleared automatically after ~1.5s.
  const [copiedFormat, setCopiedFormat] = useState(null);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchRoster = async () => {
    try {
      const res = await axios.get(`${API}/members`, { headers: authHeader() });
      const all = Array.isArray(res.data) ? res.data : [];
      // Sort by chapter then handle so the picker is easy to skim.
      const sorted = [...all].sort((a, b) => {
        const ca = (a.chapter || "").localeCompare(b.chapter || "");
        if (ca !== 0) return ca;
        return (a.handle || "").localeCompare(b.handle || "");
      });
      setRosterMembers(sorted);
    } catch (e) {
      // Silent — attendees picker is optional.
      setRosterMembers([]);
    }
  };

  const toggleAttendee = (handle) => {
    setAttendeeHandles((prev) => {
      const next = new Set(prev);
      if (next.has(handle)) next.delete(handle);
      else next.add(handle);
      return next;
    });
  };
  const clearAttendees = () => setAttendeeHandles(new Set());

  const fetchList = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/transcriptions`, { headers: authHeader() });
      setTranscriptions(res.data?.transcriptions || []);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Failed to load transcriptions");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchOne = async (id) => {
    try {
      const res = await axios.get(`${API}/transcriptions/${id}`, { headers: authHeader() });
      setSelected(res.data?.transcription || null);
      setSelectedId(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load transcription");
    }
  };

  useEffect(() => {
    fetchList();
    fetchRoster();
  }, []);

  // While any transcription is still processing in the background, poll the
  // list every 10s so it flips to completed/failed without a manual refresh.
  useEffect(() => {
    const hasProcessing = transcriptions.some((t) => t.status === "processing");
    if (!hasProcessing) return undefined;
    const iv = setInterval(() => fetchList(true), 10000);
    return () => clearInterval(iv);
  }, [transcriptions]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAudioFile(f);
    if (!meetingTitle) {
      const name = f.name.replace(/\.[^.]+$/, "");
      setMeetingTitle(name);
    }
  };

  const handleUpload = async () => {
    if (!audioFile) {
      toast.error("Choose an audio file first");
      return;
    }

    setUploading(true);
    setConversionStage(null);
    setConversionProgress(0);

    // If the user picked a video, transcode to mp3 in the browser first.
    // This saves us from uploading 1-2 GB of video over a phone hotspot
    // when ffmpeg-on-server would have stripped the video anyway.
    let fileToUpload = audioFile;
    if (isVideoFile(audioFile)) {
      try {
        fileToUpload = await videoToMp3InBrowser(audioFile, {
          onStage: (s) => setConversionStage(s),
          onProgress: (p) => setConversionProgress(p),
        });
        const ratio = audioFile.size > 0
          ? Math.max(1, Math.round(audioFile.size / fileToUpload.size))
          : 1;
        toast.success(
          `Video converted: ${formatBytes(audioFile.size)} → ${formatBytes(fileToUpload.size)} (${ratio}× smaller)`
        );
      } catch (err) {
        setUploading(false);
        setConversionStage(null);
        toast.error(`Conversion failed: ${err.message || err}`);
        return;
      }
    }

    setConversionStage(null);
    const formData = new FormData();
    formData.append("file", fileToUpload);
    if (meetingTitle) formData.append("title", meetingTitle);
    if (meetingDate) formData.append("meeting_date", meetingDate);

    // Pass the selected attendees so the LLM has a closed candidate set when
    // attributing speaker turns. Backend stores the list and threads it into
    // the meeting-notes prompt.
    const attendeesPayload = rosterMembers
      .filter((m) => attendeeHandles.has(m.handle))
      .map((m) => ({ handle: m.handle, title: m.title || "", chapter: m.chapter || "" }));
    if (attendeesPayload.length > 0) {
      formData.append("attendees", JSON.stringify(attendeesPayload));
    }

    try {
      await axios.post(`${API}/transcriptions/upload`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        // Upload only — transcription runs in the background on the server.
        timeout: 10 * 60 * 1000,
      });
      toast.success(
        "Upload received — transcribing in the background. It will appear in the list below when ready (you can leave this page)."
      );
      setAudioFile(null);
      setMeetingTitle("");
      setAttendeeHandles(new Set());
      if (fileRef.current) fileRef.current.value = "";
      await fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
    } finally {
      setUploading(false);
      setConversionStage(null);
      setConversionProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this transcription? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/transcriptions/${id}`, { headers: authHeader() });
      toast.success("Deleted");
      if (selectedId === id) {
        setSelected(null);
        setSelectedId(null);
      }
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const handleCopy = async (fmt, text, label) => {
    const value = (text || "").trim();
    if (!value) {
      toast.error(`${label} is empty — nothing to copy`);
      return;
    }
    const writeWithLegacy = () => {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      if (!ok) throw new Error("execCommand copy failed");
    };
    try {
      // Prefer async clipboard API; if the browser denies permission (common
      // in embedded webviews / iframes), silently fall through to the
      // execCommand fallback rather than surfacing a failure toast.
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(value);
        } catch {
          writeWithLegacy();
        }
      } else {
        writeWithLegacy();
      }
      setCopiedFormat(fmt);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => {
        setCopiedFormat((cur) => (cur === fmt ? null : cur));
      }, 1500);
    } catch (e) {
      toast.error("Copy failed — you may need to grant clipboard access");
    }
  };

  const handleDownload = async (id, fmt) => {
    try {
      const res = await axios.get(`${API}/transcriptions/${id}/pdf/${fmt}`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const match = dispo.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || `transcription_${fmt}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white px-3 py-4 sm:px-4 sm:py-6 md:p-8 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/")}
          className="text-slate-400 hover:text-white -ml-2"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Back to Dashboard</span>
          <span className="sm:hidden">Back</span>
        </Button>
      </div>

      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold flex items-center gap-2 sm:gap-3">
            <Mic className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400 shrink-0" />
            <span className="break-words">Meeting Transcriptions</span>
          </h1>
          <p className="text-slate-400 mt-1 text-sm sm:text-base">
            Upload meeting audio or video — we&apos;ll transcribe it with Whisper
            and generate a detailed transcript, summary, and meeting notes in
            the BOH master template format. Large files supported (1 GB+).
          </p>
        </div>

        {/* Upload card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-white text-base sm:text-lg">
              <Upload className="w-5 h-5 text-amber-400 shrink-0" />
              Upload Meeting Audio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-3 sm:px-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="meeting-title" className="text-slate-300">Meeting Title</Label>
                <Input
                  id="meeting-title"
                  data-testid="meeting-title-input"
                  value={meetingTitle}
                  onChange={(e) => setMeetingTitle(e.target.value)}
                  placeholder="e.g. HA Officer Meeting"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label htmlFor="meeting-date" className="text-slate-300">Meeting Date</Label>
                <Input
                  id="meeting-date"
                  data-testid="meeting-date-input"
                  type="date"
                  value={meetingDate}
                  onChange={(e) => setMeetingDate(e.target.value)}
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Audio, Video, or Craig Zip File</Label>
              <div className="mt-1 flex flex-col gap-2">
                <input
                  ref={fileRef}
                  data-testid="audio-file-input"
                  type="file"
                  accept="audio/*,video/*,.zip,.mp3,.wav,.m4a,.ogg,.flac,.aac,.opus,.webm,.mp4,.mov,.mkv,.avi,.wmv,.flv,.3gp,.mpeg"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-600 file:text-white hover:file:bg-amber-700 cursor-pointer"
                />
                {audioFile && (
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    <FileAudio className="w-3.5 h-3.5" />
                    <span data-testid="selected-file-info">
                      {audioFile.name} · {formatBytes(audioFile.size)}
                    </span>
                  </div>
                )}
                <p className="text-[11px] text-slate-500">
                  Accepts audio (mp3 / wav / m4a / ogg / flac / aac / opus),
                  video (mp4 / mov / mkv / avi / webm / flv), and{" "}
                  <span className="text-amber-400">Craig bot .zip recordings</span> — the
                  zip's per-speaker tracks give exact "who said what" attribution.
                  Video is converted to MP3 on your device first (saves data ~70×) — only
                  the audio is uploaded. Large files supported.
                </p>
              </div>
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                Attendees (helps AI identify speakers)
                {attendeeHandles.size > 0 && (
                  <span className="text-xs text-amber-400 font-normal">
                    · {attendeeHandles.size} selected
                  </span>
                )}
              </Label>
              <p className="text-[11px] text-slate-500 mt-1 mb-2">
                Pick everyone who attended. The AI will map speaker turns to these names instead of inventing them.
              </p>
              <div className="flex gap-2 mb-2">
                <Input
                  data-testid="attendees-filter-input"
                  value={attendeeFilter}
                  onChange={(e) => setAttendeeFilter(e.target.value)}
                  placeholder="Filter by handle or chapter…"
                  className="bg-slate-900 border-slate-600 text-white text-sm h-9"
                />
                {attendeeHandles.size > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearAttendees}
                    className="text-slate-400 hover:text-white shrink-0 h-9"
                    data-testid="clear-attendees-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
              {/* Selected chips */}
              {attendeeHandles.size > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {[...attendeeHandles].slice(0, 30).map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => toggleAttendee(h)}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-amber-900/40 text-amber-200 border border-amber-700 hover:bg-amber-900/60"
                      data-testid={`selected-attendee-${h}`}
                    >
                      {h} ×
                    </button>
                  ))}
                  {attendeeHandles.size > 30 && (
                    <span className="text-[11px] text-slate-500 px-2 py-0.5">
                      +{attendeeHandles.size - 30} more
                    </span>
                  )}
                </div>
              )}
              {/* Roster list */}
              <div
                className="max-h-40 overflow-y-auto border border-slate-700 rounded-md bg-slate-900/60 p-1"
                data-testid="attendees-roster-list"
              >
                {rosterMembers
                  .filter((m) => {
                    if (!attendeeFilter.trim()) return true;
                    const q = attendeeFilter.toLowerCase();
                    return (
                      (m.handle || "").toLowerCase().includes(q) ||
                      (m.chapter || "").toLowerCase().includes(q) ||
                      (m.title || "").toLowerCase().includes(q)
                    );
                  })
                  .slice(0, 100)
                  .map((m) => {
                    const checked = attendeeHandles.has(m.handle);
                    return (
                      <label
                        key={m.id}
                        className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${
                          checked ? "bg-amber-900/30" : "hover:bg-slate-800"
                        }`}
                        data-testid={`attendee-row-${m.handle}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttendee(m.handle)}
                          className="accent-amber-500"
                        />
                        <span className="text-white">{m.handle}</span>
                        {m.title && (
                          <span className="text-[10px] uppercase tracking-wider text-amber-400">
                            {m.title}
                          </span>
                        )}
                        {m.chapter && (
                          <span className="text-[10px] text-slate-500 ml-auto">{m.chapter}</span>
                        )}
                      </label>
                    );
                  })}
                {rosterMembers.length === 0 && (
                  <div className="text-xs text-slate-500 p-2">Roster loading…</div>
                )}
              </div>
            </div>

            <Button
              onClick={handleUpload}
              disabled={!audioFile || uploading}
              data-testid="start-transcription-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white w-full"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {conversionStage
                    ? `Converting video… ${
                        conversionProgress > 0
                          ? `${Math.round(conversionProgress * 100)}%`
                          : ""
                      }`
                    : "Uploading…"}
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  {isVideoFile(audioFile) ? "Convert + Transcribe" : "Start Transcription"}
                </>
              )}
            </Button>
            {conversionStage && (
              <div
                className="text-[11px] text-amber-300 text-center -mt-1"
                data-testid="conversion-stage"
              >
                {conversionStage}
                {conversionProgress > 0 && (
                  <div className="mt-1 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                    <div
                      className="h-full bg-amber-500 transition-all duration-150"
                      style={{ width: `${Math.round(conversionProgress * 100)}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transcription list */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="text-white text-base sm:text-lg">Your Transcriptions</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {loading ? (
              <div className="text-slate-400 text-sm">Loading…</div>
            ) : transcriptions.length === 0 ? (
              <div className="text-slate-400 text-sm" data-testid="no-transcriptions">
                No transcriptions yet — upload an audio file above to get started.
              </div>
            ) : (() => {
              // Group transcriptions by chapter so each chapter gets its own
              // folder/tab. Normalise the chapter label (handles the AD/HA/HS
              // shorthand AND the full "Asphalt Demons" / "Highway Asylum"
              // form that older entries may carry).
              const normaliseChapter = (raw) => {
                const c = (raw || "").trim();
                if (!c) return "Unfiled";
                if (c === "Asphalt Demons" || c === "AD") return "AD";
                if (c === "Highway Asylum" || c === "HA") return "HA";
                if (c === "Highway Souls" || c === "HS") return "HS";
                if (c === "National") return "National";
                return c;
              };
              const buckets = {};
              transcriptions.forEach((t) => {
                const k = normaliseChapter(t.chapter);
                if (!buckets[k]) buckets[k] = [];
                buckets[k].push(t);
              });
              // Preferred order: AD, HA, HS, National, then any others alphabetically, "Unfiled" last.
              const preferredOrder = ["AD", "HA", "HS", "National"];
              const allChapterKeys = Object.keys(buckets);
              const ordered = [
                ...preferredOrder.filter((k) => buckets[k]),
                ...allChapterKeys
                  .filter((k) => !preferredOrder.includes(k) && k !== "Unfiled")
                  .sort(),
                ...(buckets["Unfiled"] ? ["Unfiled"] : []),
              ];
              const visibleList =
                activeChapterFolder === "All"
                  ? transcriptions
                  : buckets[activeChapterFolder] || [];
              return (
                <>
                  {/* Folder tabs — one per chapter that has at least 1 entry, plus All. */}
                  <div
                    className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-slate-700"
                    data-testid="chapter-folder-tabs"
                  >
                    <button
                      type="button"
                      onClick={() => setActiveChapterFolder("All")}
                      data-testid="chapter-folder-All"
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        activeChapterFolder === "All"
                          ? "bg-amber-600 text-white border-amber-500"
                          : "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800"
                      }`}
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                      <span>All</span>
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-800/80 text-[10px] text-slate-300">
                        {transcriptions.length}
                      </span>
                    </button>
                    {ordered.map((chapKey) => {
                      const count = buckets[chapKey].length;
                      const active = activeChapterFolder === chapKey;
                      return (
                        <button
                          key={chapKey}
                          type="button"
                          onClick={() => setActiveChapterFolder(chapKey)}
                          data-testid={`chapter-folder-${chapKey}`}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                            active
                              ? "bg-amber-600 text-white border-amber-500"
                              : "bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800"
                          }`}
                        >
                          {active ? (
                            <FolderOpen className="w-3.5 h-3.5" />
                          ) : (
                            <Folder className="w-3.5 h-3.5" />
                          )}
                          <span>{chapKey}</span>
                          <span
                            className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
                              active ? "bg-amber-700 text-amber-100" : "bg-slate-800/80 text-slate-300"
                            }`}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Filtered list */}
                  {visibleList.length === 0 ? (
                    <div className="text-slate-400 text-sm" data-testid="empty-chapter-folder">
                      No transcriptions in this folder yet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visibleList.map((t) => (
                        <div
                          key={t.id}
                          data-testid={`transcription-row-${t.id}`}
                          className={`flex items-center justify-between gap-2 p-3 rounded-md border ${
                            selectedId === t.id
                              ? "border-amber-500 bg-slate-900"
                              : "border-slate-700 bg-slate-900/50 hover:bg-slate-900"
                          } cursor-pointer`}
                          onClick={() => {
                            if (t.status === "processing") {
                              toast.info("Still transcribing — it will be ready shortly.");
                              return;
                            }
                            fetchOne(t.id);
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-white truncate text-sm sm:text-base flex items-center gap-2">
                              <span className="truncate">{t.title}</span>
                              {t.status === "processing" && (
                                <span
                                  className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300 text-[10px] uppercase tracking-wide"
                                  data-testid={`processing-badge-${t.id}`}
                                >
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Processing
                                </span>
                              )}
                              {t.status === "failed" && (
                                <span
                                  className="inline-flex items-center shrink-0 px-1.5 py-0.5 rounded bg-red-900/50 text-red-300 text-[10px] uppercase tracking-wide"
                                  title={t.error || "Transcription failed"}
                                  data-testid={`failed-badge-${t.id}`}
                                >
                                  Failed
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] sm:text-xs text-slate-400 mt-0.5 truncate">
                              {t.chapter || "—"} · {t.meeting_date}
                              {t.status === "processing"
                                ? " · transcribing…"
                                : ` · ${formatDuration(t.duration_seconds)}`}
                              <span className="hidden sm:inline">
                                {" "}· by {t.uploaded_by_handle || t.uploaded_by_username}
                              </span>
                            </div>
                            {t.status === "failed" && t.error && (
                              <div className="text-[11px] text-red-400 mt-0.5 truncate">{t.error}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-950/30 shrink-0 h-9 w-9 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(t.id);
                            }}
                            data-testid={`delete-transcription-${t.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Selected transcription detail */}
        {selected && (
          <Card className="bg-slate-800 border-slate-700" data-testid="transcription-detail">
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="text-white text-lg sm:text-xl break-words">{selected.title}</CardTitle>
              <div className="text-[11px] sm:text-xs text-slate-400 mt-1">
                <span className="block sm:inline">{selected.chapter || "—"}</span>
                <span className="hidden sm:inline"> · </span>
                <span className="block sm:inline">Meeting: {selected.meeting_date}</span>
                <span className="hidden sm:inline"> · </span>
                <span className="block sm:inline">Duration: {formatDuration(selected.duration_seconds)}</span>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <Tabs defaultValue="meeting">
                <TabsList className="bg-slate-900 border border-slate-700 grid grid-cols-3 w-full h-auto p-1">
                  {FORMAT_META.map((m) => (
                    <TabsTrigger
                      key={m.key}
                      value={m.key}
                      data-testid={`tab-${m.key}`}
                      className="data-[state=active]:bg-amber-600 data-[state=active]:text-white text-[11px] sm:text-sm px-1 sm:px-3 py-2 flex flex-col sm:flex-row items-center gap-1 sm:gap-2"
                    >
                      <m.icon className="w-4 h-4 shrink-0" />
                      <span className="leading-none">{m.label}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>
                {FORMAT_META.map((m) => (
                  <TabsContent key={m.key} value={m.key} className="mt-4">
                    <div className="mb-3 flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleDownload(selected.id, m.key)}
                        data-testid={`download-${m.key}-pdf`}
                        className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download {m.label} PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCopy(m.key, selected[m.field], m.label)}
                        data-testid={`copy-${m.key}-btn`}
                        className="border-slate-600 bg-slate-900 text-slate-100 hover:bg-slate-800 hover:text-white w-full sm:w-auto"
                      >
                        {copiedFormat === m.key ? (
                          <>
                            <Check className="w-4 h-4 mr-2 text-emerald-400" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy {m.label}
                          </>
                        )}
                      </Button>
                    </div>
                    <MarkdownView text={selected[m.field]} testId={`content-${m.key}`} />
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
